import type { ReactNode } from 'react';
import { cn } from '../../utils';

export interface SubHeaderProps {
  children: ReactNode;
  /** Sticky to top of scrolling container. Default: true. */
  sticky?: boolean;
  /** Extra className on the outer wrapper. */
  className?: string;
  /** Accessible label on the toolbar. */
  ariaLabel?: string;
}

export interface SubHeaderZoneProps {
  children?: ReactNode;
  className?: string;
}

const ZONE_BASE = 'flex items-center gap-2 min-w-0';

/**
 * 4-zone sub-header — canonical page chrome per DESIGN_RULES §5.
 *
 * Zones are optional and render in order: Names · Stats · Filters · Actions.
 * Any zone can be omitted; remaining zones collapse accordingly.
 *
 * ```tsx
 * <SubHeader>
 *   <SubHeader.Names>
 *     <h1 className="text-[17px] font-semibold">Invoices</h1>
 *   </SubHeader.Names>
 *   <SubHeader.Stats>
 *     <Pill>{count} invoices</Pill>
 *   </SubHeader.Stats>
 *   <SubHeader.Filters>
 *     <SearchInput />
 *   </SubHeader.Filters>
 *   <SubHeader.Actions>
 *     <Button leftIcon={<Plus />}>New invoice</Button>
 *   </SubHeader.Actions>
 * </SubHeader>
 * ```
 */
export function SubHeader({
  children,
  sticky = true,
  className,
  ariaLabel = 'Page toolbar',
}: SubHeaderProps) {
  return (
    <div
      aria-label={ariaLabel}
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2',
        'px-[22px] py-2',
        'border-b border-[var(--color-border)] bg-[var(--color-bg)]',
        sticky && 'sticky top-0 z-[90]',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Names({ children, className }: SubHeaderZoneProps) {
  return (
    <div
      data-zone="1"
      className={cn(
        ZONE_BASE,
        // Zone #1 is the identity — give it room to shrink gracefully.
        'flex-1 basis-[220px] text-[var(--color-text-primary)]',
        className,
      )}
    >
      {children}
    </div>
  );
}
Names.displayName = 'SubHeader.Names';

function Stats({ children, className }: SubHeaderZoneProps) {
  return (
    <div
      data-zone="2"
      className={cn(
        ZONE_BASE,
        'text-[12px] text-[var(--color-text-secondary)] tabular-nums',
        className,
      )}
    >
      {children}
    </div>
  );
}
Stats.displayName = 'SubHeader.Stats';

function Filters({ children, className }: SubHeaderZoneProps) {
  return (
    <div data-zone="3" className={cn(ZONE_BASE, 'flex-1 basis-[180px] justify-end', className)}>
      {children}
    </div>
  );
}
Filters.displayName = 'SubHeader.Filters';

function Actions({ children, className }: SubHeaderZoneProps) {
  return (
    <div data-zone="4" className={cn(ZONE_BASE, 'ml-auto shrink-0 justify-end', className)}>
      {children}
    </div>
  );
}
Actions.displayName = 'SubHeader.Actions';

SubHeader.Names = Names;
SubHeader.Stats = Stats;
SubHeader.Filters = Filters;
SubHeader.Actions = Actions;
